import { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { apiFetch, getToken } from '../../hooks/useApi.js';

/**
 * "Send to the client by DPD" on the sample label screens (Joe, 15 Oct
 * 2026). Fills in the client's delivery address and main contact from
 * their record; printing the labels queues the parcel, which then goes in
 * the next DPD import file. The parent calls ref.queue() after printing.
 */
const inputStyle = {
  padding: '7px 10px', borderRadius: 'var(--radius)', border: '1px solid var(--border)', fontSize: 13,
  background: 'var(--surface)', width: '100%', boxSizing: 'border-box', color: 'var(--text)', fontFamily: 'inherit',
};
const small = { padding: '5px 10px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--text)', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' };

export async function downloadSampleDpdFile() {
  const res = await fetch('/api/sample-parcels/dpd-file.csv', { method: 'POST', headers: { Authorization: `Bearer ${getToken()}` } });
  if (!res.ok) { const b = await res.json().catch(() => ({})); throw new Error(b.error || `Couldn't make the DPD file (HTTP ${res.status})`); }
  const name = ((res.headers.get('Content-Disposition') || '').match(/filename="([^"]+)"/) || [])[1] || 'dpd-samples.csv';
  const url = URL.createObjectURL(await res.blob());
  const a = document.createElement('a'); a.href = url; a.download = name; a.click();
  setTimeout(() => URL.revokeObjectURL(url), 60000);
  return Number(res.headers.get('X-Blake-Count') || 0);
}

const SampleParcelSection = forwardRef(function SampleParcelSection({ clientName, kind, projectId, roundId, runId, reference, description, onSendChange }, ref) {
  const [send, setSend] = useState(true);
  const [r, setR] = useState(null);       // recipient fields
  const [info, setInfo] = useState(null); // what the client record had
  const [editing, setEditing] = useState(false);
  const [msg, setMsg] = useState(null);
  const [sent, setSent] = useState([]);

  useEffect(() => {
    let live = true;
    setMsg(null);
    if (!clientName) { setR(null); setInfo(null); return undefined; }
    apiFetch(`/sample-parcels/recipient?client_name=${encodeURIComponent(clientName)}`).then((d) => {
      if (!live) return;
      const rec = d?.data ?? d;
      setInfo(rec || null);
      setR(rec ? { parcels: 1, weight_kg: 1, ...rec } : null);
      setEditing(!!rec && (!rec.address_line1 || !rec.postcode));
    }).catch(() => { if (live) { setR(null); setInfo(null); } });
    return () => { live = false; };
  }, [clientName]);

  useEffect(() => {
    const q = roundId ? `round_id=${roundId}` : runId ? `production_run_id=${runId}` : null;
    if (!q) { setSent([]); return; }
    apiFetch(`/sample-parcels?${q}`).then((d) => setSent(Array.isArray(d) ? d : d?.data || [])).catch(() => {});
  }, [roundId, runId, msg]);

  const ready = !!(r && r.address_line1 && r.postcode && r.recipient_name);
  useEffect(() => { if (onSendChange) onSendChange(send && !!clientName); }, [send, clientName]); // eslint-disable-line react-hooks/exhaustive-deps

  async function queue() {
    if (!send || !clientName) return null;
    if (!ready) { setMsg({ error: 'Not sent: the address is incomplete — fill it in and press "Queue parcel".' }); setEditing(true); return null; }
    try {
      const d = await apiFetch('/sample-parcels', {
        method: 'POST',
        body: { ...r, kind, client_name: clientName, project_id: projectId, round_id: roundId, production_run_id: runId, reference, description },
      });
      const res = d?.data ?? d;
      const saved = res.saved_to_client?.length ? ` Added to ${clientName}'s record: ${res.saved_to_client.join(', ')}.` : '';
      setMsg({ ok: `${res.reused ? 'Parcel already queued — updated.' : `Parcel to ${r.recipient_company || r.recipient_name} queued. It goes in the next DPD file.`}${saved}` });
      if (saved) setInfo((x) => ({ ...x, has_address: true }));
      return res;
    } catch (e) { setMsg({ error: e.message }); return null; }
  }
  useImperativeHandle(ref, () => ({ queue, get willSend() { return send && !!clientName; } }));

  const set = (k) => (e) => setR((x) => ({ ...x, [k]: e.target.value }));
  const field = (k, label, span = 1, type = 'text') => (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 3, fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', gridColumn: `span ${span}` }}>
      {label}<input type={type} value={r?.[k] ?? ''} onChange={set(k)} style={inputStyle} />
    </label>
  );

  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: 10, padding: '12px 14px', margin: '10px 0', background: 'var(--surface-2)' }}>
      <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer', color: 'var(--text)' }}>
        <input type="checkbox" checked={send && !!clientName} disabled={!clientName} onChange={(e) => setSend(e.target.checked)} />
        Send to the client by DPD
      </label>
      {!clientName && <p style={{ margin: '6px 0 0', fontSize: 12, color: 'var(--text-muted)' }}>Choose the client above to send the samples to them.</p>}
      {clientName && send && !info && <p style={{ margin: '6px 0 0', fontSize: 12, color: '#92400e' }}>{clientName} isn&rsquo;t in Blake&rsquo;s client list — fill in where it&rsquo;s going.</p>}
      {clientName && send && r && (
        <>
          {!editing ? (
            <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', marginTop: 8, flexWrap: 'wrap' }}>
              <div style={{ flex: '1 1 260px', fontSize: 13, color: 'var(--text)', lineHeight: 1.5 }}>
                <strong>{r.recipient_name}</strong>{r.recipient_company && r.recipient_company !== r.recipient_name ? `, ${r.recipient_company}` : ''}<br />
                {[r.address_line1, r.address_line2, r.town, r.postcode].filter(Boolean).join(', ')}<br />
                <span style={{ color: 'var(--text-muted)' }}>{[r.phone, r.email].filter(Boolean).join(' · ') || 'No phone or email — DPD can’t send tracking'} · {r.parcels} parcel{Number(r.parcels) === 1 ? '' : 's'}, {r.weight_kg} kg</span>
                {info?.from_billing && <div style={{ fontSize: 12, color: '#92400e' }}>No delivery address on file — using the billing address.</div>}
              </div>
              <button type="button" style={small} onClick={() => setEditing(true)}>Change</button>
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 8, marginTop: 10 }}>
              {field('recipient_name', 'Send to *', 2)}{field('recipient_company', 'Company', 2)}
              {field('address_line1', 'Address *', 2)}{field('address_line2', 'Address line 2', 2)}
              {field('town', 'Town')}{field('postcode', 'Postcode *')}{field('phone', 'Phone')}{field('email', 'Email')}
              {field('parcels', 'Parcels', 1, 'number')}{field('weight_kg', 'Weight (kg)', 1, 'number')}
              {!info?.has_address && <p style={{ gridColumn: 'span 4', margin: 0, fontSize: 12, color: '#92400e' }}>There&rsquo;s no address on {clientName}&rsquo;s client record yet — what you enter here is saved to it.</p>}
              <div style={{ gridColumn: 'span 4', display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <button type="button" style={small} onClick={() => setEditing(false)} disabled={!ready}>Done</button>
              </div>
            </div>
          )}
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 8, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 12, color: 'var(--text-muted)', flex: '1 1 200px' }}>Printing the labels queues the parcel.</span>
            <button type="button" style={small} onClick={queue}>Queue parcel now</button>
          </div>
        </>
      )}
      {msg?.ok && (
        <div style={{ marginTop: 8, fontSize: 13, color: '#166534', display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          ✓ {msg.ok}
          <button type="button" style={small} onClick={async () => {
            try { const n = await downloadSampleDpdFile(); setMsg({ ok: `DPD file downloaded (${n} parcel${n === 1 ? '' : 's'}) — upload it in MyDPD Local.` }); } catch (e) { setMsg({ error: e.message }); }
          }}>Download DPD file now</button>
        </div>
      )}
      {msg?.error && <div style={{ marginTop: 8, fontSize: 13, color: '#b91c1c' }}>{msg.error}</div>}
      {sent.length > 0 && (
        <div style={{ marginTop: 8, fontSize: 12, color: 'var(--text-muted)' }}>
          Sent before: {sent.map((p) => `${new Date(p.created_at).toLocaleDateString('en-GB')} to ${p.recipient_company || p.recipient_name}${p.status === 'queued' ? ' (waiting for the DPD file)' : ''}`).join(' · ')}
        </div>
      )}
    </div>
  );
});

export default SampleParcelSection;
